import Store from '@store';
import { buildLinkedTextId, splitLinkedTextAreaLink } from 'src/helpers/text-overflow-links';

export class TextOverflowChainHandler {
  getLinkedTextElements () {
    const { linkedTextElements } = Store.getState().editor;

    return linkedTextElements;
  }

  getLinkedTextElementsFromPage (pageId) {
    const linkedTextElements = this.getLinkedTextElements();

    const result = [];
    for (const chain of linkedTextElements) {
      if (chain.find(element => Number(element.pageId) === Number(pageId))) {
        result.push(...chain.map(element => buildLinkedTextId(element.pageId, element.id)));
      }
    }
    return result;
  }

  getRelatedLinkedTextsFromPage (pageId) {
    const chains = this.getLinkedTextElements();

    return chains.reduce((acc, chain) => {
      return acc.concat(chain.filter((item, index, all) => {
        return item.pageId !== pageId && all[index - 1]?.pageId === pageId;
      }));
    }, []);
  }

  getElementDataFromStore (elementId, pageId) {
    const { elements } = Store.getState();

    return elements[pageId]?.[elementId] || {};
  }

  getTextDomByLinkId (linkId) {
    const [pageId, id] = splitLinkedTextAreaLink(linkId);
    const currentPageId = INKIVE.Tool._current_design_id;
    const isTextInCurrentPage = Number(pageId) === Number(currentPageId);

    return isTextInCurrentPage
      ? document.querySelector(`g#${id}`)
      : document.querySelector(`g#${id}[data-page-id="${pageId}"]`);
  }

  getTextChainByLinkId (linkId) {
    const [pageId, id] = splitLinkedTextAreaLink(linkId);
    const linkedTextElements = this.getLinkedTextElements();

    for (const chain of linkedTextElements) {
      const index = chain.findIndex(({ id: chainElementId, pageId: chainPageId }) => {
        return chainElementId === id && Number(pageId) === Number(chainPageId);
      });

      if (index > -1) return chain;
    }

    return null;
  }

  getOrderedDataFromPreviousPages (linkId) {
    const currentPageId = Number(INKIVE.Tool._current_design_id);
    const svgContent = document.getElementById('svgcontent');
    const textMock = document.getElementById('textmock');
    const chain = this.getTextChainByLinkId(linkId);
    const orderedData = {
      style: [],
      level: [],
      startFrom: [],
      numberingStyle: [],
      regex: []
    };

    if (!chain) return {};

    let isContinuing = false;
    for (const element of chain) {
      const elementLinkId = buildLinkedTextId(element.pageId, element.id);
      const elementData = Number(element.pageId) === currentPageId
        ? this.getElementDataFromDOM(svgContent.getElementById(element.id))
        : this.getElementDataFromDOM(textMock.querySelector(`#${element.id}[data-page-id="${element.pageId}"]`));

      if (!elementData.orderedList) {
        isContinuing = !elementData.isLastLineEol;
        continue;
      }

      const itemToSlice = isContinuing ? 1 : 0;

      orderedData.style = orderedData.style.concat(elementData.orderedList.style.slice(itemToSlice));
      orderedData.level = orderedData.level.concat(elementData.orderedList.level.slice(itemToSlice));
      orderedData.startFrom = orderedData.startFrom.concat(elementData.orderedList.startFrom.slice(itemToSlice));

      isContinuing = !elementData.isLastLineEol;

      if (elementLinkId === linkId) break;
    }

    return orderedData;
  }

  getElementDataFromDOM (textarea) {
    /**
     * Select all text elements that are first line of the paragraph
     */
    const orderedData = {
      orderedList: { style: [], level: [], startFrom: [], numberingStyle: [], regex: [] },
      isLastLineEol: true
    };
    if (!textarea) return orderedData;
    const headTexts = textarea.querySelectorAll('text:first-child, text.eol + text.eol, text.eol + text:not(.eol)');
    orderedData.isLastLineEol = textarea.querySelector('text:last-child')?.classList?.contains('eol');

    const hasOrderedList = !!textarea.querySelector('text[data-ordered]');
    if (!hasOrderedList || headTexts === null || !headTexts.length) return orderedData;

    headTexts.forEach(text => {
      const style = Number(text.dataset.orderedStyle) || 0;
      const level = Number(text.dataset.ordered) || 0;
      const startFrom = Number(text.dataset.orderedStart) || 0;
      const numberingStyle = text.dataset.orderedNumberingStyle || 0;
      const regex = text.dataset.orderedRegex || '';

      orderedData.orderedList.style.push(style);
      orderedData.orderedList.level.push(level);
      orderedData.orderedList.startFrom.push(startFrom);
      orderedData.orderedList.numberingStyle.push(numberingStyle);
      orderedData.orderedList.regex.push(regex);
    });

    return orderedData;
  }

  findTextLink (elementId, pageId = INKIVE.Tool._current_design_id) {
    const { linkedTextElements } = Store.getState().editor;

    return linkedTextElements.reduce((acc, chain) => {
      if (acc) return acc;

      const index = chain.findIndex(({ id, pageId: chainPageId }) => id === elementId && pageId === chainPageId);

      if (index > -1) {
        return {
          parent: chain[index - 1],
          child: chain[index + 1]
        };
      }

      return acc;
    }, null);
  }

  addTextElementToTextMock (elementId, pageId) {
    const elementData = this.getElementDataFromStore(elementId, pageId);
    const textMock = document.getElementById('textmock');

    if (!textMock || !elementData?.nodeString) return null;

    const existingElement = textMock?.querySelector(`#${elementId}[data-page-id="${pageId}"]`);
    if (existingElement) return existingElement;

    textMock.insertAdjacentHTML('beforeend', elementData.nodeString);
    return textMock?.lastChild;
  }
}
